import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import userLogService from '../../../services/userLogService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faDownload,
    faSync,
    faCircle,
    faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import Table from '../../../components/Common/Table/Table';
import Heading from '../../../components/Common/Heading/Heading';
import ListScreenSearchBar from '../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';
import './UserLogs.css';

const parseLogDate = (value) => {
    if (value == null || value === '') return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const formatDurationHMS = (ms) => {
    let n = typeof ms === 'number' ? ms : 0;
    if (n < 0) n = 0;
    const totalSec = Math.floor(n / 1000);
    const hours = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const CHICAGO_IANA = 'America/Chicago';

/** YYYY-MM-DD in Chicago, for comparing clock-in vs clock-out calendar days. */
const formatCstDateKey = (value) => {
    const d = parseLogDate(value);
    if (!d) return '';
    return d.toLocaleDateString('en-CA', { timeZone: CHICAGO_IANA });
};

const MS_PER_DAY = 86400000;

/** When clock-out is earlier than clock-in on the same stored calendar day, assume next day(s) until span is positive. */
const sessionSpanMsClosedSession = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return 0;
    const start = clockIn.getTime();
    let end = clockOut.getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return 0;
    let guard = 0;
    while (end <= start && guard < 14) {
        end += MS_PER_DAY;
        guard += 1;
    }
    return Math.max(0, end - start);
};

const formatSessionDurationFromLog = (log) => {
    const clockIn = parseLogDate(log?.clock_in);
    if (!clockIn) return 'N/A';
    const clockOut = parseLogDate(log?.clock_out);
    if (clockOut) {
        return formatDurationHMS(sessionSpanMsClosedSession(clockIn, clockOut));
    }
    const updatedAt = parseLogDate(log?.updated_at);
    const end = updatedAt && updatedAt >= clockIn ? updatedAt : new Date();
    return formatDurationHMS(Math.max(0, end.getTime() - clockIn.getTime()));
};

const UserLogs = () => {
    const { updateBreadcrumbs } = useBreadcrumb();
    
    // State
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [search, setSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState('10');
    const [sortBy, setSortBy] = useState('clock_in');
    const [sortOrder, setSortOrder] = useState('desc');

    const limit = useMemo(
        () => (entriesPerPage === 'all' ? 9999 : parseInt(entriesPerPage, 10) || 10),
        [entriesPerPage]
    );
    const entriesSelectValue = entriesPerPage === 'all' ? 'all' : String(entriesPerPage);
    const fromDateInputRef = useRef(null);
    const toDateInputRef = useRef(null);
    const startDateIso = startDate || '';
    const endDateIso = endDate || '';

    const openDatePicker = (ref) => {
        const el = ref?.current;
        if (!el) return;
        try {
            el.showPicker?.();
        } catch {
            el.focus();
        }
    };

    useLayoutEffect(() => {
        updateBreadcrumbs([
            { label: 'Admin List', path: '/admin' },
            { label: 'User Logs', path: null, active: true }
        ]);
    }, [updateBreadcrumbs]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const filters = {
                page,
                limit,
                search: debouncedSearch,
                startDate: startDate || '',
                endDate: endDate || '',
                sortBy,
                sortOrder
            };
            const response = await userLogService.getUserLogs(filters);
            if (response.success) {
                setLogs(response.logs);
                setTotalPages(response.pagination.total_pages);
                setTotalLogs(response.pagination.total);
            }
        } catch (error) {
            console.error('Failed to fetch user logs:', error);
        } finally {
            setLoading(false);
        }
    }, [page, limit, debouncedSearch, startDate, endDate, sortBy, sortOrder]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleStartDateChange = (val) => {
        setStartDate(val);
        setPage(1);
    };

    const handleEndDateChange = (val) => {
        setEndDate(val);
        setPage(1);
    };

    const handleExport = async () => {
        try {
            const filters = {
                search: debouncedSearch,
                startDate: startDate || '',
                endDate: endDate || '',
                sortBy,
                sortOrder
            };
            await userLogService.exportLogs(filters);
        } catch (error) {
            console.error('Failed to export user logs:', error);
        }
    };

    /** CST Time Formatter */
    const formatToCST = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return 'N/A';
        return date.toLocaleString('en-US', {
            timeZone: CHICAGO_IANA,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    const getStatusBadge = (status) => {
        const dot = (extraClass = '') => (
            <FontAwesomeIcon
                icon={faCircle}
                className={['status-pill-dot', extraClass].filter(Boolean).join(' ')}
            />
        );
        switch (status) {
            case 'Active':
                return (
                    <span className="status-pill status-pill--active">
                        {dot('status-pill-dot--pulse')} Active
                    </span>
                );
            case 'Not Active':
                return (
                    <span className="status-pill status-pill--inactive">
                        {dot()} Not Active
                    </span>
                );
            case 'User logged out':
                return (
                    <span className="status-pill status-pill--logged-out">
                        {dot()} User logged out
                    </span>
                );
            default:
                return (
                    <span className="status-pill status-pill--default">
                        {dot()} {status}
                    </span>
                );
        }
    };

    const handleSort = (columnKey) => {
        if (!columnKey) return;
        if (sortBy === columnKey) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(columnKey);
            setSortOrder('asc');
        }
        setPage(1);
    };

    const columns = [
        {
            title: 'User Name',
            key: 'user_name',
            sortable: true,
            className: 'user-name-cell font-weight-bold',
            render: (log) => log.user_name || 'N/A'
        },
        {
            title: 'Username',
            key: 'username',
            sortable: true,
            className: 'username-cell text-muted',
            render: (log) => log.username ? `@${log.username}` : 'N/A'
        },
        {
            title: 'Clock In (CST)',
            key: 'clock_in',
            sortable: true,
            className: 'clock-in-cell time-cell',
            render: (log) => formatToCST(log.clock_in)
        },
        {
            title: 'Clock Out (CST)',
            key: 'clock_out',
            sortable: true,
            className: 'clock-out-cell time-cell',
            render: (log) => {
                if (!log.clock_out) return '---';
                const full = formatToCST(log.clock_out);
                const inDay = formatCstDateKey(log.clock_in);
                const outDay = formatCstDateKey(log.clock_out);
                if (inDay && outDay && inDay !== outDay) {
                    return full;
                }
                const parts = full.split(', ');
                return parts.length > 1 ? parts[1] : parts[0];
            } 
        },
        {
            title: 'Session Duration',
            key: 'duration',
            sortable: true,
            className: 'duration-cell time-cell',
            render: (log) => formatSessionDurationFromLog(log)
        },
        { title: 'IP Address', key: 'ip_address', sortable: true, className: 'ip-cell text-muted' },
        {
            title: 'Status',
            key: 'status',
            sortable: true,
            className: 'user-logs-col-status reusable-table-centered-col',
            render: (log) => getStatusBadge(log.status)
        }
    ];

    return (
        <div className="user-logs-page list-screen-page-container">
            {/* Header Top Bar */}
            <div className="user-logs-header list-screen-top-bar">
                <div className="list-screen-title-section">
                    <Heading title="User Logs" level="h2" className="user-logs-title list-screen-title-text" />
                    <p className="user-logs-subtitle">Monitoring User Access and Session Activity</p>
                </div>
                <div className="list-screen-actions">
                    <button type="button" className="export-btn" onClick={handleExport}>
                        <FontAwesomeIcon icon={faDownload} /> Export CSV
                    </button>
                    <button type="button" className="reset-btn" onClick={fetchLogs} disabled={loading}>
                        <FontAwesomeIcon icon={faSync} spin={loading} /> Refresh
                    </button>
                </div>
            </div>

            {/* Filter Section Card */}
            <div className="user-logs-filters">
                <div className="filter-row">
                    <div className="filter-item search-filter">
                        <ListScreenSearchBar
                            value={search}
                            onChange={(val) => setSearch(val)}
                            inputId="user-logs-search"
                        />
                    </div>

                    <div className="filter-item date-filter">
                        <div className="date-picker-group">
                            <div className="date-picker-wrap">
                                <span className="date-label">From:</span>
                                <div className="user-log-date-field">
                                    <input
                                        ref={fromDateInputRef}
                                        type="date"
                                        value={startDateIso}
                                        onChange={(e) => handleStartDateChange(e.target.value)}
                                        max={endDateIso || undefined}
                                        onClick={(e) => {
                                            try {
                                                e.currentTarget.showPicker?.();
                                            } catch {
                                                /* ignore */
                                            }
                                        }}
                                        className="form-input-custom user-log-date-input"
                                    />
                                    <button
                                        type="button"
                                        className="user-log-date-calendar-btn"
                                        aria-label="Open calendar"
                                        onClick={() => openDatePicker(fromDateInputRef)}
                                    >
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                    </button>
                                </div>
                            </div>
                            <div className="date-picker-wrap">
                                <span className="date-label">To:</span>
                                <div className="user-log-date-field">
                                    <input
                                        ref={toDateInputRef}
                                        type="date"
                                        value={endDateIso}
                                        onChange={(e) => handleEndDateChange(e.target.value)}
                                        min={startDateIso || ''}
                                        max={undefined}
                                        onClick={(e) => {
                                            try {
                                                e.currentTarget.showPicker?.();
                                            } catch {
                                                /* ignore */
                                            }
                                        }}
                                        className="form-input-custom user-log-date-input"
                                    />
                                    <button
                                        type="button"
                                        className="user-log-date-calendar-btn"
                                        aria-label="Open calendar"
                                        onClick={() => openDatePicker(toDateInputRef)}
                                    >
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table & Pagination Card Container */}
            <div className="user-logs-table-container">
                <Table
                    columns={columns}
                    data={logs}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    isLoading={loading}
                    emptyMessage="No session logs found matching the criteria."
                    loadingMode="skeleton"
                />

                {!loading && totalLogs > 0 && (
                    <ListScreenPaginationBar
                        entriesValue={entriesSelectValue}
                        onEntriesChange={(value) => {
                            setEntriesPerPage(value);
                            setPage(1);
                        }}
                        showPageSelector={entriesPerPage !== 'all'}
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                    />
                )}
            </div>
        </div>
    );
};

export default UserLogs;
